/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * Page Tariffs And Services Selectors
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.1
 * @since 1.4.0
 */

import find from 'lodash/find';
import uniq from 'lodash/uniq';
import { createSelector, OutputSelector } from 'reselect';

import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import { AuthorityRoleEnum } from '../PageAuth/constants/authConstants';
import { getAuthorities, makeSelectAuthoritiesIsCoordinator } from '../PageAuth/selectors/authSelectors';
import { IAuthority } from '../PageAuth/types/authTypes';
import {
    EProductsCart,
    EProductsCategory,
    EProductsPageFrom,
    EProductsPayment,
    EProductsTab,
} from './tariffsAndServicesConstants';
import { IProductCardService } from './tariffsAndServicesProductServiceTypes';
import { ICardTariffNewItem, IProductCardTariffsRangeSelected } from './tariffsAndServicesProductTariffTypes';
import { TProductItem } from './tariffsAndServicesProductTypes';
import { IProductOfferingsMap, IProductsState } from './tariffsAndServicesReducer';
import {
    IProductCategoryIds,
    IProductsCategory,
    IProductsMeta,
    IProductsRegionItem,
    IProductsRegionParams,
    IProductsSelected,
    IProductsWithCategoryList,
    IRoamingCountry,
    IRoamingOption,
} from './tariffsAndServicesTypes';
import { TariffsAndServicesCategoryGraph } from './utils/categoryGraph';

export const makeSelectProductsState = (state: IRootReducer): IProductsState => state.products;

/**
 * Вернет тип текущей корзины
 */
export const makeSelectProductsCart = (): OutputSelector<
    IRootReducer,
    EProductsCart,
    (state: IProductsState) => EProductsCart
> => createSelector(makeSelectProductsState, (state) => state.cart);

/**
 * Subscriber id
 */
export const makeSelectProductsClientId = (): OutputSelector<
    IRootReducer,
    string | undefined,
    (state: IProductsState) => string | undefined
> => createSelector(makeSelectProductsState, (state) => state.clientId);

/**
 * Список всех категорий услуг
 */
export const makeSelectProductsCategories = (): OutputSelector<
    IRootReducer,
    IProductsCategory[],
    (state: IProductsState) => IProductsCategory[]
> => createSelector(makeSelectProductsState, (state) => state.categoryList);

/**
 * Выводит список услуг с результатом поиска
 */
export const makeSelectTariffsAndServicesFoundItems = () =>
    createSelector<IRootReducer, EProductsPayment, TProductItem[], string, TProductItem[]>(
        [
            makeSelectProductsPaymentSelected(),
            makeSelectProductsList(),
            makeSelectProductsSearchValue(),
        ],
        (paymentSelected, productsList, searchValue) => {
            return productsList.filter((item) => {
                if (item.isTariff || item.personalOfferingId !== undefined) {
                    return false;
                }
                if (paymentSelected !== EProductsPayment.ALL) {
                    const isAvailable = filterProductByFee(item, paymentSelected);
                    if (!isAvailable) {
                        return false;
                    }
                }
                return item.title.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0;
            });
        },
    );

export const makeSelectProductCategoryListIsLoading = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state: IProductsState) => state.categoryListIsLoading,
    );

export const makeSelectProductCategoryListIsError = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state: IProductsState) => state.categoryListIsError,
    );

/**
 * Вернут список id категорий, где были отмечены услуги на выбраной вкладке
 */
export const makeSelectProductsCategoriesIdsWhereItemsSelected = () =>
    createSelector<IRootReducer, TProductItem[], number[], IProductsCategory[], number[]>(
        [
            makeSelectProductsList(),
            makeSelectProductsItemsSelectedIds(),
            makeSelectProductsCategories(), // для построения дерева, нужен весь список
        ],
        (productList, itemsSelectedAvailable, categoryList) => {
            const childSelectedCategories = uniq(
                productList
                    .filter((product) => itemsSelectedAvailable.includes(product.id))
                    .reduce((accum, currentService) => accum.concat(currentService.categories), [] as number[]),
            );

            const categoryGraph = new TariffsAndServicesCategoryGraph(categoryList);

            return categoryGraph.getAllSelectedCategories(childSelectedCategories);
        },
    );

/**
 * Вернет список категорий, где есть выбранные услуги и на выбранной вкладке
 */
export const makeSelectTariffsAndServicesCategoryListSelected = () =>
    createSelector(
        [makeSelectProductsCategories(), makeSelectProductsCategoriesIdsWhereItemsSelected()],
        (categoryList, categoriesIds) => {
            return categoryList.filter((category) => categoriesIds.includes(category.id));
        },
    );

export const makeSelectTariffsAndServicesCategoryWithServicesList = (filtered = false) =>
    createSelector(
        [
            makeSelectProductsCategorySelected(),
            makeSelectProductsCategories(),
            makeSelectProductsList(),
            makeSelectProductsPaymentSelected(),
            makeSelectProductsItemsSelectedIds(),
            makeSelectProductsMeta(),
        ],
        (
            categorySelected,
            categoryList,
            productList,
            paymentSelected,
            itemsSelected,
            productsMeta,
        ) => {
            const getProductListByCategoryId = (id: number) =>
                productList.filter((item) => {
                    if (filtered && paymentSelected !== EProductsPayment.ALL) {
                        const isAvailable = filterProductByFee(item, paymentSelected);
                        if (!isAvailable) {
                            return false;
                        }
                    }
                    if (categorySelected === EProductsCategory.SELECTED) {
                        return itemsSelected.includes(item.id);
                    }
                    return item.categories.indexOf(id) >= 0;
                });

            const resultList: IProductsWithCategoryList = {};

            // on choosing the root service category, need to fill the product list of "Selected services"
            if (categorySelected === productsMeta.servicesCategoryId) {
                const chosenCategory = categoryList.find((item) => item.id === EProductsCategory.SELECTED);
                if (chosenCategory) {
                    resultList[EProductsCategory.SELECTED] = {
                        category: chosenCategory,
                        productList: productList.filter((product: TProductItem) => itemsSelected.includes(product.id)),
                    };
                }
            }

            const children = categoryList.filter((item) => item.parent === categorySelected);
            if (children.length) {
                children.forEach((child) => {
                    const servicesList = getProductListByCategoryId(child.id);
                    if (servicesList.length) {
                        if (!resultList[child.id]) {
                            resultList[child.id] = {
                                category: child,
                                productList: [],
                            };
                        }
                        resultList[child.id].productList = servicesList;
                    }
                });
            }

            const category = categoryList.find((item: IProductsCategory) => item.id === categorySelected);
            if (category) {
                const servicesList = getProductListByCategoryId(categorySelected);

                const childrenWithNotEmptyServicesList = children.filter((item) => resultList[item.id]);
                if (servicesList.length || (!servicesList.length && !childrenWithNotEmptyServicesList.length)) {
                    resultList[categorySelected] = {
                        category: category,
                        productList: servicesList,
                    };
                }
            }
            return resultList;
        },
    );

export const makeSelectPreselectedTariffsCategory = () =>
    createSelector(
        [
            makeSelectProductsCategories(),
            makeSelectProductsList(),
            makeSelectProductsCategoryIds(),
            makeSelectProductsMeta(),
        ],
        (categoryList, productList, productCategoryIds, productsMeta) => {
            if (!productsMeta.tariffsCategoryId || !productCategoryIds.tariffsCartegoryIds.length) return null;

            /* находим доступные id категорий для текущего productList
               (pruductList формируется в зависимости от того как пришел пользователь на страницу) */
            const availableProductCategoryIds = new Set(productList.map((el) => el.categories).flat());

            // из всех категорий фильтруем и сортируем вложеные категории тарифов (с учетом доступных id)
            const sortedTariffsCateogies = categoryList
                .filter(
                    (el) =>
                        el.id !== productsMeta.tariffsCategoryId &&
                        productCategoryIds.tariffsCartegoryIds.includes(el.id) &&
                        availableProductCategoryIds.has(el.id),
                )
                .sort((a, b) => a.order - b.order);

            if (!sortedTariffsCateogies.length) return null;

            /* если в sortedTariffsCateogies присутствует activeCategoryTariffId, то возвращаем id
               для предвыбора категории, иначе возвращаем id первой категории из sortedTariffsCateogies */
            const preselectedCategory =
                sortedTariffsCateogies.find((el) => el.id === productsMeta.activeCategoryTariffId) ||
                sortedTariffsCateogies[0];

            return preselectedCategory.id;
        },
    );

export const makeSelectProductsListAvailable = () =>
    createSelector<IRootReducer, IProductsState, EProductsPageFrom, TProductItem[]>(
        [makeSelectProductsState, makeSelectProductsPageFrom()],
        (state, pageFrom) => {
            if (pageFrom === EProductsPageFrom.SUBSCRIBERS) {
                return state.productListAvailable.filter((item: TProductItem) => item.available);
            }
            return state.productListAvailable;
        },
    );

export const makeSelectProductsListConnected = () =>
    createSelector<IRootReducer, IProductsState, TProductItem[]>(
        makeSelectProductsState,
        (state) => state.productListConnected,
    );

export const makeSelectProductsList = () =>
    createSelector<IRootReducer, TProductItem[], TProductItem[], EProductsTab, TProductItem[]>(
        [makeSelectProductsListAvailable(), makeSelectProductsListConnected(), makeSelectProductsTabSelected()],
        (productListAvailable, productListConnected, tabSelected) => {
            return tabSelected === EProductsTab.AVAILABLE ? productListAvailable : productListConnected;
        },
    );

/**
 * Получить подробный массив выбранных услуг на текущей вкладке
 */
export const makeSelectProductsProductsListSelected = () =>
    createSelector([makeSelectProductsList(), makeSelectProductsItemsSelectedIds()], (productsList, itemsSelected) => {
        return productsList.filter((item) => itemsSelected.includes(item.id));
    });

/**
 * Вернет id выбранной категории
 */
export const makeSelectProductsCategorySelected = (): OutputSelector<
    IRootReducer,
    number,
    (state: IProductsState) => number
> => createSelector(makeSelectProductsState, (state) => state.categorySelected);

export const makeSelectProductsSearchValue = () =>
    createSelector<IRootReducer, IProductsState, string>(makeSelectProductsState, (state) => state.searchValue);

/**
 * Предварительный список услуг при поиске
 */
export const makeSelectTariffsAndServicesSearchedItems = () =>
    createSelector<IRootReducer, TProductItem[], string, TProductItem[]>(
        [makeSelectProductsList(), makeSelectProductsSearchValue()],
        (productList, searchValue) => {
            if (!searchValue) return [];
            return productList
                .filter((item) => !item.isTariff && item.personalOfferingId === undefined)
                .filter((item) => item.title.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
        },
    );

/**
 * Получить массив id выбранных услуг (доступных для подключения)
 */
export const makeSelectProductsItemsSelectedAvailable = () =>
    createSelector<IRootReducer, IProductsState, IProductsSelected[]>(
        makeSelectProductsState,
        (state) => state.itemsSelectedAvailable,
    );
export const makeSelectProductsItemsSelectedAvailableIds = () =>
    createSelector<IRootReducer, IProductsState, number[]>(makeSelectProductsState, (state) =>
        state.itemsSelectedAvailable.map((item) => item.id),
    );

/**
 * Получить массив id выбранных услуг (подключенных)
 */
export const makeSelectProductsItemsSelectedConnected = () =>
    createSelector<IRootReducer, IProductsState, IProductsSelected[]>(
        makeSelectProductsState,
        (state) => state.itemsSelectedConnected,
    );
export const makeSelectProductsItemsSelectedConnectedIds = () =>
    createSelector<IRootReducer, IProductsState, number[]>(makeSelectProductsState, (state) =>
        state.itemsSelectedConnected.map((item) => item.id),
    );

/**
 * Получить массив id выбранных услуг на текущей вкладке
 */
export const makeSelectProductsItemsSelected = () =>
    createSelector(
        [
            makeSelectProductsTabSelected(),
            makeSelectProductsItemsSelectedAvailable(),
            makeSelectProductsItemsSelectedConnected(),
        ],
        (tabSelected, itemsSelectedAvailable, itemsSelectedConnected) => {
            return tabSelected === EProductsTab.AVAILABLE ? itemsSelectedAvailable : itemsSelectedConnected;
        },
    );
export const makeSelectProductsItemsSelectedIds = () =>
    createSelector(
        [
            makeSelectProductsTabSelected(),
            makeSelectProductsItemsSelectedAvailableIds(),
            makeSelectProductsItemsSelectedConnectedIds(),
        ],
        (tabSelected, itemsSelectedAvailable, itemsSelectedConnected) => {
            return tabSelected === EProductsTab.AVAILABLE ? itemsSelectedAvailable : itemsSelectedConnected;
        },
    );

export const makeSelectProductsTariffSelected = () =>
    createSelector<IRootReducer, IProductsState, IProductsSelected>(
        makeSelectProductsState,
        (state) => state.itemsSelectedTariffId,
    );
export const makeSelectProductsTariffSelectedId = () =>
    createSelector<IRootReducer, IProductsState, number>(
        makeSelectProductsState,
        (state) => state.itemsSelectedTariffId.id,
    );

export const makeSelectProductsTariffsRange = () =>
    createSelector<IRootReducer, IProductsState, IProductCardTariffsRangeSelected>(
        makeSelectProductsState,
        (state) => state.itemsSelectedTariffsRange,
    );

export const makeSelectProductsTariffSelectedRange = () =>
    createSelector<IRootReducer, number, IProductCardTariffsRangeSelected, number>(
        makeSelectProductsTariffSelectedId(),
        makeSelectProductsTariffsRange(),
        (itemsSelectedTariffId, itemsSelectedTariffRange) => itemsSelectedTariffRange[itemsSelectedTariffId],
    );

export const makeSelectProductsPaymentSelected = () =>
    createSelector<IRootReducer, IProductsState, EProductsPayment>(
        makeSelectProductsState,
        (state) => state.paymentSelected,
    );

export const makeSelectProductsTabSelected = () =>
    createSelector<IRootReducer, IProductsState, EProductsTab>(makeSelectProductsState, (state) => state.tabSelected);

export const makeSelectProductsRegionParams = () =>
    createSelector<IRootReducer, IProductsState, IProductsRegionParams>(makeSelectProductsState, (state) => ({
        regionList: state.regionList,
        regionLoading: state.regionLoading,
        regionSelected: state.regionSelected,
    }));

export const makeSelectProductsRegionSelected = () =>
    createSelector<IRootReducer, IProductsState, IProductsRegionItem>(
        makeSelectProductsState,
        (state) => state.regionSelected,
    );

export const makeSelectProductsRegionIsDisabled = () =>
    createSelector<IRootReducer, IProductsState, boolean>(makeSelectProductsState, (state) => state.regionIsDisabled);

export const makeSelectProductsRegionIsError = () =>
    createSelector<IRootReducer, IProductsState, boolean>(makeSelectProductsState, (state) => state.regionIsError);

export const makeSelectTariffsAndServicesAllowedConnectTariff = () =>
    createSelector<IRootReducer, IAuthority[], boolean, boolean>(
        getAuthorities,
        makeSelectAuthoritiesIsCoordinator(),
        (authorities, isCoordinator) => {
            const isSubscriberCanChangeTariff = !!authorities.find(
                (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE_RATEPLAN,
            );

            return isSubscriberCanChangeTariff || isCoordinator;
        },
    );

export const makeSelectProductsAllowedManageProducts = () =>
    createSelector<IRootReducer, IAuthority[], boolean, boolean>(
        getAuthorities,
        makeSelectAuthoritiesIsCoordinator(),
        (authorities, isCoordinator) => {
            const isSubscriberCanChangServices = !!authorities.find(
                (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE,
            );

            return isSubscriberCanChangServices || isCoordinator;
        },
    );

export const makeSelectTariffsAndServicesIsFooterVisible = () =>
    createSelector<IRootReducer, number, IProductsMeta, IProductCategoryIds, boolean, IAuthority[], boolean>(
        [
            makeSelectProductsCategorySelected(),
            makeSelectProductsMeta(),
            makeSelectProductsCategoryIds(),
            makeSelectAuthoritiesIsCoordinator(),
            getAuthorities,
        ],
        (categorySelected, productsMeta, productCategoryIds, isCoordinator, authorities) => {
            const isTariff: boolean =
                categorySelected === productsMeta.tariffsCategoryId ||
                categorySelected === productsMeta.campaignTariffsCategoryId ||
                productCategoryIds.tariffsCartegoryIds.includes(categorySelected) ||
                productCategoryIds.campaignTariffsCartegoryIds.includes(categorySelected);

            const isSubscriberCanChangServices = !!authorities.find(
                (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE,
            );

            const isSubscriberCanChangeTariff = !!authorities.find(
                (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE_RATEPLAN,
            );

            const isCustomUser = !!find(authorities, (item) => item.authority === AuthorityRoleEnum.ROLE_CUSTOM_USER);

            const isCustomRoleRatePlanManage =
                !!find(authorities, (item) => item.authority === AuthorityRoleEnum.ROLE_RATEPLAN_MANAGE);

            const isCustomRoleServicesManage =
                !!find(authorities, (item) => item.authority === AuthorityRoleEnum.ROLE_SERVICES_MANAGE);

            return (isSubscriberCanChangServices && (!isTariff || isSubscriberCanChangeTariff))
                    || isCoordinator
                    || (isCustomUser && isCustomRoleRatePlanManage)
                    || (isCustomUser && isCustomRoleServicesManage);
        },
    );

export const makeSelectProductsIsSubsDifferentRegions = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.regionList.length > 1);

export const makeSelectProductsFilterRegionVisible = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.filterRegionVisible);

export const makeSelectProductsFilterShowSelectedOnly = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.filterShowSelectedOnly);

export const makeSelectTariffsAndServicesProductOfferingsMap = (): OutputSelector<
    IRootReducer,
    IProductOfferingsMap,
    (state: IProductsState) => IProductOfferingsMap
> => createSelector(makeSelectProductsState, (state) => state.productOfferingsMap);

export const makeSelectTariffsAndServicesProductOfferingsIsLoading = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.productOfferingsIsLoading);

export const makeSelectTariffsAndServicesProductOfferingsErrorText = (): OutputSelector<
    IRootReducer,
    string,
    (state: IProductsState) => string
> => createSelector(makeSelectProductsState, (state) => state.errorText);

export const makeSelectTariffsAndServicesProductOfferingsAvailableIsError = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.productOfferingsAvailableIsError);

export const makeSelectTariffsAndServicesProductOfferingsConnectedIsError = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IProductsState) => boolean
> => createSelector(makeSelectProductsState, (state) => state.productOfferingsConnectedIsError);

export const makeSelectProductsMeta = (): OutputSelector<
    IRootReducer,
    IProductsMeta,
    (state: IProductsState) => IProductsMeta
> => createSelector(makeSelectProductsState, (state) => state.meta);

export const makeSelectProductsPageFrom = (): OutputSelector<
    IRootReducer,
    EProductsPageFrom,
    (state: IProductsState) => EProductsPageFrom
> => createSelector(makeSelectProductsState, (state) => state.pageFrom);

export const makeSelectProductsCategoryIds = (): OutputSelector<
    IRootReducer,
    IProductCategoryIds,
    (state: IProductsState) => IProductCategoryIds
> => createSelector(makeSelectProductsState, (state) => state.productCategoryIds);

export const makeSelectProductsIsRateplans = () =>
    createSelector(
        [
            makeSelectProductsMeta(),
            makeSelectProductsCategorySelected(),
            makeSelectProductsCategoryIds(),
        ],
        (meta, categorySelected, categoryIds) => {
            const isTariff: boolean =
                categorySelected === meta.tariffsCategoryId ||
                categorySelected === meta.campaignTariffsCategoryId ||
                categoryIds.tariffsCartegoryIds.includes(categorySelected) ||
                categoryIds.campaignTariffsCartegoryIds.includes(categorySelected);

            return isTariff;
        },
    );

export const makeSelectProductsButtonProductManageTitle = () =>
    createSelector(
        [
            makeSelectProductsTabSelected(),
            makeSelectTranslationPageProducts(),
            makeSelectProductsCategorySelected(),
            makeSelectProductsMeta(),
            makeSelectProductsCategoryIds(),
        ],
        (tabSelected, translation, categorySelected, productsMeta, productCategoryIds) => {
            const { titleDisable, titleConnect, titleTariff } = translation.buttonProductManage;
            const isTariff: boolean =
                categorySelected === productsMeta.tariffsCategoryId ||
                categorySelected === productsMeta.campaignTariffsCategoryId ||
                productCategoryIds.tariffsCartegoryIds.includes(categorySelected) ||
                productCategoryIds.campaignTariffsCartegoryIds.includes(categorySelected);

            return isTariff ? titleTariff : tabSelected === EProductsTab.AVAILABLE ? titleConnect : titleDisable;
        },
    );

export const makeSelectProductsIsButtonProductManageDisabled = () =>
    createSelector(
        [
            makeSelectProductsMeta(),
            makeSelectProductsItemsSelectedIds(),
            makeSelectProductsTariffSelectedId(),
            makeSelectProductsCategorySelected(),
            makeSelectProductsCategoryIds(),
        ],
        (meta, itemsSelected, tariffSelected, categorySelected, categoryIds) => {
            const isTariff: boolean =
                categorySelected === meta.tariffsCategoryId ||
                categorySelected === meta.campaignTariffsCategoryId ||
                categoryIds.tariffsCartegoryIds.includes(categorySelected) ||
                categoryIds.campaignTariffsCartegoryIds.includes(categorySelected);

            return isTariff ? !tariffSelected : !itemsSelected.length || itemsSelected.length > meta.servicesLimit;
        },
    );

export const makeSelectProductsButtonProductManageTooltipMessage = () =>
    createSelector(
        [makeSelectProductsMeta(), makeSelectProductsItemsSelectedIds(), makeSelectTranslationPageProducts()],
        (meta, itemsSelected, translation) => {
            const { servicesLimitText } = translation.buttonProductManage;

            if (itemsSelected.length > meta.servicesLimit) {
                return stringFormatParams(servicesLimitText, {
                    servicesLimit: meta.servicesLimit,
                });
            }

            return '';
        },
    );

/**
 * Count all selected items in products dashboard
 */
export const makeSelectProductsItemsSelectedCount = () =>
    createSelector(
        [makeSelectProductsItemsSelectedAvailableIds(), makeSelectProductsItemsSelectedConnectedIds()],
        (itemsSelectedAvailable: number[], itemsSelectedConnected: number[]) =>
            itemsSelectedAvailable.length + itemsSelectedConnected.length,
    );

/**
 * Фильтрация продуктов по абоентской плате (АП)
 * Если продукт не доступен (флаг available), то он не должен попадать в выборку при фильтрации
 */
const filterProductByFee = (
    item: TProductItem,
    paymentSelected: EProductsPayment,
): boolean => {
    if (!item.isTariff) {
        const productService = item as IProductCardService;
        if (!productService.productOfferingRecurringCharges) {
            return false;
        }
        if (
            paymentSelected === EProductsPayment.WITH_FEE &&
            (!productService.productOfferingRecurringCharges.find((item) => item.price > 0) || !item.available)
        ) {
            return false;
        }
        if (
            paymentSelected === EProductsPayment.NO_FEE &&
            (productService.productOfferingRecurringCharges.find((item) => item.price > 0) || !item.available)
        ) {
            return false;
        }
    }

    if (item.isTariff) {
        const productTariff = item as ICardTariffNewItem;
        if (
            paymentSelected === EProductsPayment.WITH_FEE &&
            (!productTariff.payment || !productTariff.payment.value.value || productTariff.payment.value.value < 0)
        ) {
            return false;
        }
        if (
            paymentSelected === EProductsPayment.NO_FEE &&
            productTariff.payment &&
            productTariff.payment.value.value &&
            productTariff.payment.value.value > 0
        ) {
            return false;
        }
    }
    // if this function returns true, then the product does not match the fee filter, but may not match other parameters
    return true;
};

export const makeSelectRoamingOptionsCountries = () =>
    createSelector<IRootReducer, IProductsState, IRoamingCountry[]>(
        makeSelectProductsState,
        (state) => state.roamingOptionsCountries,
    );

export const makeSelectRoamingOptionsCountriesIsLoading = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state) => state.roamingOptionsCountriesIsLoading,
    );

export const makeSelectSelectedCountryCode = () =>
    createSelector<IRootReducer, IProductsState, string>(
        makeSelectProductsState,
        (state) => state.selectedCountryCode,
    );

export const makeSelectRoamingOptionsSelected = () =>
    createSelector<IRootReducer, IProductsState, IRoamingOption[]>(
        makeSelectProductsState,
        (state) => state.roamingOptionsSelected,
    );

export const makeSelectRoamingOptions= () =>
    createSelector<IRootReducer, IProductsState, IRoamingOption[]>(
        makeSelectProductsState,
        (state) => state.roamingOptions,
    );

export const makeSelectRoamingOptionsIsLoading = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state) => state.roamingOptionsIsLoading,
    );

export const makeSelectIncludeCampaignServiceProducts = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state) => state.includeCampaignServiceProducts,
    );

export const makeSelectIncludeCampaignTariffProducts = () =>
    createSelector<IRootReducer, IProductsState, boolean>(
        makeSelectProductsState,
        (state) => state.includeCampaignTariffProducts,
    );
